import FieldForm from "../form/selected/field/FieldForm";
import ImageForm from "../form/selected/image/ImageForm";
import QrForm from "../form/selected/qr/QrForm";
import TextForm from "../form/selected/text/TextForm";
import BoxForm from "../form/selected/box/BoxForm";
import TextLabelItem from "./text/TextLabelItem";
import BoxLabelItem from "./box/BoxLabelItem";
import FieldLabelLabelItem from "./field/label/FieldLabelLabelItem";
import ImageLabelItem from "./image/ImageLabelItem";
import QrLabelItem from "./qr/QrLabelItem";
import VisFieldForm from "../form/selected/vis_field/VisFieldForm";
import VisFieldLabelItem from "./vis_field/VisFieldLabelItem";
import FieldValuesForm from "../form/selected/field/values/FieldValuesForm";
import FieldValuesLabelItem from "./field/values/FieldValuesLabelItem";
import { useAuthContext } from "../../../../context/AuthContext";

/**
 * @returns {Array<string>} - An array of available label item types.
 */
export function useLabelItemTypes() {
  const authContext = useAuthContext();

  const types = [
    "text",
    "field_label",
    "field_values",
    "image",
    "registration_code_qr",
    "box",

    "company_name",
    "location_name",
    "location_address",
    "visitor_type_name",
    "language_label",
    "sign_in_terminal_name",
    "employee_name",
    "employee_first_name",
    "employee_last_name",
    "employee_title",
    "email",
    "registration_code",
    "returning",
    "expected_date",
    "expected_end_date",
    "expected_time",
    "signed_in_at",
    "signed_out_at",
    "status",
    "license_plate",
  ];

  if (authContext.auth.user?.company?.salto_config) {
    types.push("salto_user_pin");
  }

  if (authContext.auth?.user?.company?.access_commander_config) {
    types.push("access_commander_2n_pin");
    types.push("access_commander_2n_pin_qr")
  }
  return types;
}

/**
 * This method returns the form component for a specific label item type.
 * @param {string} type
 * @returns {JSX.Element|null}
 */
export function getLabelItemForm(type) {
  switch (type) {
    case "text":
      return TextForm;
    case "field_label":
      return FieldForm;
    case "field_values":
      return FieldValuesForm;
    case "image":
      return ImageForm;
    case "registration_code_qr":
    case "access_commander_2n_pin_qr":
      return QrForm;
    case "box":
      return BoxForm;
    case "company_name":
    case "location_name":
    case "location_address":
    case "visitor_type_name":
    case "language_label":
    case "sign_in_terminal_name":
    case "employee_title":
    case "employee_name":
    case "employee_first_name":
    case "employee_last_name":
    case "email":
    case "registration_code":
    case "returning":
    case "expected_date":
    case "expected_end_date":
    case "expected_time":
    case "signed_in_at":
    case "signed_out_at":
    case "status":
    case "license_plate":
    case "salto_user_pin":
    case "access_commander_2n_pin":
      return VisFieldForm;
    default:
      return null;
  }
}

/**
 * This method will return the initial data for a label item based on its type.
 * @param {string} type
 * @returns {object}
 */
export function getInitLabelItemData(type) {
  switch (type) {
    case "text":
    case "field_label":
    case "company_name":
    case "location_name":
    case "location_address":
    case "visitor_type_name":
    case "language_label":
    case "sign_in_terminal_name":
    case "employee_title":
    case "employee_name":
    case "employee_first_name":
    case "employee_last_name":
    case "email":
    case "registration_code":
    case "returning":
    case "expected_date":
    case "expected_end_date":
    case "expected_time":
    case "signed_in_at":
    case "signed_out_at":
    case "status":
    case "salto_user_pin":
    case "license_plate":
    case "access_commander_2n_pin":
      return {
        width: 22,
        height: 3,
        font_size: 7,
        font_weight: "normal",
        text_align: "left",
      };

    case "field_values":
      return {
        width: 22,
        height: 3,
        font_size: 7,
        font_weight: "normal",
        text_align: "left",
        fields: [],
      };

    case "image":
      return {
        width: 20,
        height: 10,
      };

    case "registration_code_qr":
    case "access_commander_2n_pin_qr":
      return {
        width: 12,
        height: 12,
      };

    case "box":
      return {
        width: 70,
        height: 0.5,
      };

    default:
      return {};
  }
}

/**
 * This method will return the content of a label item based on its type.
 * @param {object} item
 * @param {object} printerData
 * @param {object} visitorData
 * @param {function} translate
 * @param {function} translateEntity
 * @param {Array} fields
 * @param {Array} images
 * @param {boolean} readOnly
 * @returns {JSX.Element|null}
 */
export function getLabelItemContent(
  item,
  printerData,
  visitorData,
  fields,
  images,
  readOnly
) {
  let Component = null;

  switch (item.type) {
    case "text":
      Component = TextLabelItem;
      break;
    case "box":
      Component = BoxLabelItem;
      break;
    case "field_label":
      Component = FieldLabelLabelItem;
      break;
    case "field_values":
      Component = FieldValuesLabelItem;
      break;
    case "image":
      Component = ImageLabelItem;
      break;
    case "registration_code_qr":
    case "access_commander_2n_pin_qr":
      Component = QrLabelItem;
      break;
    case "company_name":
    case "location_name":
    case "location_address":
    case "visitor_type_name":
    case "language_label":
    case "sign_in_terminal_name":
    case "employee_title":
    case "employee_name":
    case "employee_first_name":
    case "employee_last_name":
    case "email":
    case "registration_code":
    case "returning":
    case "expected_date":
    case "expected_end_date":
    case "expected_time":
    case "signed_in_at":
    case "signed_out_at":
    case "status":
    case "license_plate":
    case "salto_user_pin":
    case "access_commander_2n_pin":
      Component = VisFieldLabelItem;
      break;
    default:
      Component = null;
      break;
  }

  if (!Component) {
    return null;
  }

  return (
    <Component
      item={item}
      printerData={printerData}
      visitorData={visitorData}
      fields={fields}
      images={images}
      readOnly={readOnly}
    />
  );
}
